/**
 * Type patterns: parsing and combining (docs/9-exploration/TYPE-PATTERNS-PRD.md §3).
 *
 * Owner ruling 2026-10-15: no part has a winner. Each assumption stays attached
 * to the type that set it:
 *   - workload adds up line by line; each line keeps its source's own effort
 *     multiplier (engineering's ×1.5 never inflates marketing's hours);
 *   - shapes line up on their anchor; one type's stages never merge into another's;
 *   - a single-valued field (how fixed the date is, schedule slack …) stated by
 *     two types that disagree is shown to the item's owner to settle. Until they
 *     do, planning reads the cautious value and the field is flagged.
 * An item's own override, part by part, beats every type.
 *
 * Pure: no store, no database. Callers pass what they read.
 */

import {
  APPROACH_KINDS,
  EMPTY_PATTERN,
  type ApproachKind,
  type BarPart,
  type CertaintyPart,
  type DateFirmness,
  type ItemPatternOverride,
  type ItemTypes,
  type PatternAnchor,
  type PatternParts,
  type PatternPart,
  type QualityBar,
  type ShapePart,
  type ShapeStage,
  type TypeList,
  type TypePattern,
  type WorkloadLine,
  type WorkloadPart,
} from '../types/prioritization/typePatterns';

// ── Parsing (stored jsonb → typed parts; anything malformed is dropped) ──────

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const strs = (v: unknown): string[] | undefined =>
  Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : undefined;
const anchor = (v: unknown): PatternAnchor | undefined => (v === 'start' || v === 'date' ? v : undefined);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;

export const DATE_FIRMNESS: DateFirmness[] = ['fixed', 'target', 'flexible'];
export const QUALITY_BARS: QualityBar[] = ['polished', 'good_enough', 'rough'];

export function parseCertainty(raw: unknown): CertaintyPart {
  const r = obj(raw);
  const out: CertaintyPart = {};
  const slack = num(r.scheduleSlackWeeks);
  if (slack !== undefined && slack >= 0) out.scheduleSlackWeeks = slack;
  const ceiling = num(r.confidenceCeiling);
  if (ceiling !== undefined && ceiling >= 0 && ceiling <= 1) out.confidenceCeiling = ceiling;
  const approach = oneOf<ApproachKind>(r.approach, APPROACH_KINDS);
  if (approach) out.approach = approach;
  const mult = num(r.effortMultiplier);
  if (mult !== undefined && mult > 0) out.effortMultiplier = mult;
  const before = strs(r.beforeScheduling);
  if (before?.length) out.beforeScheduling = before;
  return out;
}

export function parseShape(raw: unknown): ShapePart {
  const r = obj(raw);
  const out: ShapePart = {};
  const a = anchor(r.anchor);
  if (a) out.anchor = a;
  if (Array.isArray(r.stages)) {
    const stages = r.stages
      .map((s): ShapeStage | null => {
        const o = obj(s);
        const label = typeof o.label === 'string' ? o.label.trim() : '';
        const weeks = num(o.weeks);
        return label && weeks !== undefined && weeks > 0 ? { label, weeks } : null;
      })
      .filter((s): s is ShapeStage => s !== null);
    if (stages.length) out.stages = stages;
  }
  return out;
}

export function parseWorkload(raw: unknown): WorkloadPart {
  const r = obj(raw);
  const out: WorkloadPart = {};
  const a = anchor(r.anchor);
  if (a) out.anchor = a;
  if (Array.isArray(r.lines)) {
    const lines = r.lines
      .map((l): WorkloadLine | null => {
        const o = obj(l);
        const resourceType = typeof o.resourceType === 'string' ? o.resourceType.trim() : '';
        const capacityUnits = num(o.capacityUnits);
        const fromWeek = num(o.fromWeek);
        const toWeek = num(o.toWeek);
        if (!resourceType || capacityUnits === undefined || capacityUnits <= 0) return null;
        if (fromWeek === undefined || toWeek === undefined || toWeek < fromWeek) return null;
        return { resourceType, capacityUnits, fromWeek: Math.round(fromWeek), toWeek: Math.round(toWeek) };
      })
      .filter((l): l is WorkloadLine => l !== null);
    if (lines.length) out.lines = lines;
  }
  return out;
}

export function parseBar(raw: unknown): BarPart {
  const r = obj(raw);
  const out: BarPart = {};
  const date = oneOf(r.date, DATE_FIRMNESS);
  if (date) out.date = date;
  const quality = oneOf(r.quality, QUALITY_BARS);
  if (quality) out.quality = quality;
  const firmness = num(r.firmness);
  if (firmness !== undefined && firmness >= 1 && firmness <= 10) out.firmness = Math.round(firmness);
  const signOff = strs(r.signOff);
  if (signOff?.length) out.signOff = signOff;
  return out;
}

export function parsePatternParts(raw: {
  certainty?: unknown;
  shape?: unknown;
  workload?: unknown;
  bar?: unknown;
}): PatternParts {
  return {
    certainty: parseCertainty(raw.certainty),
    shape: parseShape(raw.shape),
    workload: parseWorkload(raw.workload),
    bar: parseBar(raw.bar),
  };
}

/** Default anchor when a part does not name one: launch types hang off the date, others off the start. */
export function defaultAnchorFor(listKey: string): PatternAnchor {
  return listKey === 'launch_type' ? 'date' : 'start';
}

export function isEmptyPattern(p: PatternParts): boolean {
  return PATTERN_PARTS_LIST.every((part) => Object.keys(p[part]).length === 0);
}

const PATTERN_PARTS_LIST: PatternPart[] = ['certainty', 'shape', 'workload', 'bar'];

// ── Combining ─────────────────────────────────────────────────────────────────

/** Where an assumption came from: a type in a list, or the item itself. */
export interface AssumptionSource {
  listKey: string;
  listName: string;
  typeValue: string;
  typeLabel: string;
  /** The pattern version read; null for a work type's own row or an override. */
  patternId: string | null;
  patternVersion: number | null;
}

export interface FieldAssumption<T> {
  part: PatternPart;
  field: string;
  /** Every type that states this field, with its value. */
  stated: Array<{ source: AssumptionSource; value: T }>;
  /** The item's own answer, when it has one. */
  override: { value: T; reason: string | null } | null;
  /** True when types disagree and the item has no override: its owner should pick. */
  needsChoice: boolean;
  /** What planning reads: the override, the agreed value, or the cautious one. */
  effective: T | undefined;
}

export interface SourcedShape {
  source: AssumptionSource;
  anchor: PatternAnchor;
  stages: ShapeStage[];
}

export interface SourcedWorkloadLine extends WorkloadLine {
  source: AssumptionSource;
  anchor: PatternAnchor;
  /** This line's source's effort multiplier (1 when none). Applied once, at demand time. */
  effortMultiplier: number;
}

export interface ItemAssumptions {
  slackWeeks: FieldAssumption<number>;
  confidenceCeiling: FieldAssumption<number>;
  approach: FieldAssumption<ApproachKind>;
  date: FieldAssumption<DateFirmness>;
  quality: FieldAssumption<QualityBar>;
  firmness: FieldAssumption<number>;
  /** All of them apply; nothing to choose between. */
  beforeScheduling: Array<{ source: AssumptionSource; text: string }>;
  signOff: Array<{ source: AssumptionSource; text: string }>;
  shapes: SourcedShape[];
  workload: SourcedWorkloadLine[];
  /** True when no type the item has carries anything. */
  empty: boolean;
}

/** The work type's own row (canon 111): approach and effort multiplier live there. */
export interface WorkTypeRecipeFacts {
  approach?: string | null;
  effortMultiplier?: number | null;
}

export interface ResolveInput {
  lists: TypeList[];
  itemTypes: ItemTypes;
  /** Active pattern per list key + type value. */
  patterns: TypePattern[];
  /** Live overrides for this item. */
  overrides: ItemPatternOverride[];
  /** Label of a type in a list, for display. */
  typeLabel?: (listKey: string, value: string) => string;
  /** Approach and multiplier for a work type, from its own row. */
  workTypeFacts?: (value: string) => WorkTypeRecipeFacts | null;
}

const FIRMNESS_RANK: Record<DateFirmness, number> = { fixed: 3, target: 2, flexible: 1 };
const QUALITY_RANK: Record<QualityBar, number> = { polished: 3, good_enough: 2, rough: 1 };

function field<T>(
  part: PatternPart,
  name: string,
  stated: Array<{ source: AssumptionSource; value: T }>,
  overrides: ItemPatternOverride[],
  cautious: (values: T[]) => T,
  validate: (v: unknown) => T | undefined,
): FieldAssumption<T> {
  const o = overrides.find((x) => x.part === part && x.field === name);
  const ov = o ? validate(o.value) : undefined;
  const override = ov !== undefined && o ? { value: ov, reason: o.reason } : null;
  const distinct = [...new Set(stated.map((s) => JSON.stringify(s.value)))];
  const needsChoice = !override && distinct.length > 1;
  const effective = override
    ? override.value
    : stated.length === 0
      ? undefined
      : distinct.length === 1
        ? stated[0].value
        : cautious(stated.map((s) => s.value));
  return { part, field: name, stated, override, needsChoice, effective };
}

/**
 * An item's assumptions from every type it has, with their sources.
 * Lists are read in their own order (sort order), work types first by default.
 */
export function resolveItemAssumptions(input: ResolveInput): ItemAssumptions {
  const label = input.typeLabel ?? ((_k: string, v: string) => v);
  const byKey = new Map(input.patterns.map((p) => [`${p.listKey}::${p.typeValue}`, p]));
  const lists = [...input.lists].filter((l) => l.isActive).sort((a, b) => a.sortOrder - b.sortOrder);

  const slack: Array<{ source: AssumptionSource; value: number }> = [];
  const ceiling: Array<{ source: AssumptionSource; value: number }> = [];
  const approach: Array<{ source: AssumptionSource; value: ApproachKind }> = [];
  const date: Array<{ source: AssumptionSource; value: DateFirmness }> = [];
  const quality: Array<{ source: AssumptionSource; value: QualityBar }> = [];
  const firmness: Array<{ source: AssumptionSource; value: number }> = [];
  const beforeScheduling: ItemAssumptions['beforeScheduling'] = [];
  const signOff: ItemAssumptions['signOff'] = [];
  const shapes: SourcedShape[] = [];
  const workload: SourcedWorkloadLine[] = [];

  for (const list of lists) {
    const value = input.itemTypes[list.key];
    if (!value) continue;
    const pattern = byKey.get(`${list.key}::${value}`);
    const parts: PatternParts = pattern ?? EMPTY_PATTERN;
    const source: AssumptionSource = {
      listKey: list.key,
      listName: list.name,
      typeValue: value,
      typeLabel: label(list.key, value),
      patternId: pattern?.id ?? null,
      patternVersion: pattern?.versionNumber ?? null,
    };

    // Work types keep approach and multiplier on their own row (canon 111).
    const facts = list.key === 'work_type' ? input.workTypeFacts?.(value) ?? null : null;
    const ownApproach = list.key === 'work_type'
      ? oneOf<ApproachKind>(facts?.approach, APPROACH_KINDS)
      : parts.certainty.approach;
    const ownMultiplier = list.key === 'work_type'
      ? (num(facts?.effortMultiplier) ?? undefined)
      : parts.certainty.effortMultiplier;

    if (parts.certainty.scheduleSlackWeeks !== undefined) slack.push({ source, value: parts.certainty.scheduleSlackWeeks });
    if (parts.certainty.confidenceCeiling !== undefined) ceiling.push({ source, value: parts.certainty.confidenceCeiling });
    if (ownApproach) approach.push({ source, value: ownApproach });
    for (const text of parts.certainty.beforeScheduling ?? []) beforeScheduling.push({ source, text });
    if (parts.bar.date) date.push({ source, value: parts.bar.date });
    if (parts.bar.quality) quality.push({ source, value: parts.bar.quality });
    if (parts.bar.firmness !== undefined) firmness.push({ source, value: parts.bar.firmness });
    for (const text of parts.bar.signOff ?? []) signOff.push({ source, text });

    if (parts.shape.stages?.length) {
      shapes.push({ source, anchor: parts.shape.anchor ?? defaultAnchorFor(list.key), stages: parts.shape.stages });
    }
    for (const line of parts.workload.lines ?? []) {
      workload.push({
        ...line,
        source,
        anchor: parts.workload.anchor ?? defaultAnchorFor(list.key),
        effortMultiplier: ownMultiplier && ownMultiplier > 0 ? ownMultiplier : 1,
      });
    }
  }

  const ov = input.overrides;
  const numberIn = (min: number, max: number) => (v: unknown) => {
    const n = num(v);
    return n !== undefined && n >= min && n <= max ? n : undefined;
  };
  const result: ItemAssumptions = {
    slackWeeks: field('certainty', 'scheduleSlackWeeks', slack, ov, (v) => Math.max(...v), numberIn(0, 52)),
    confidenceCeiling: field('certainty', 'confidenceCeiling', ceiling, ov, (v) => Math.min(...v), numberIn(0, 1)),
    approach: field('certainty', 'approach', approach, ov, (v) => v[0], (v) => oneOf(v, APPROACH_KINDS)),
    date: field('bar', 'date', date, ov,
      (v) => v.reduce((a, b) => (FIRMNESS_RANK[b] > FIRMNESS_RANK[a] ? b : a)),
      (v) => oneOf(v, DATE_FIRMNESS)),
    quality: field('bar', 'quality', quality, ov,
      (v) => v.reduce((a, b) => (QUALITY_RANK[b] > QUALITY_RANK[a] ? b : a)),
      (v) => oneOf(v, QUALITY_BARS)),
    firmness: field('bar', 'firmness', firmness, ov, (v) => Math.max(...v), numberIn(1, 10)),
    beforeScheduling,
    signOff,
    shapes,
    workload,
    empty: false,
  };
  result.empty =
    slack.length + ceiling.length + approach.length + date.length + quality.length + firmness.length +
      beforeScheduling.length + signOff.length + shapes.length + workload.length === 0 &&
    ov.length === 0;
  return result;
}

/** The fields that need the item's owner to pick (types disagree, no override yet). */
export function fieldsNeedingChoice(a: ItemAssumptions): FieldAssumption<unknown>[] {
  return [a.slackWeeks, a.confidenceCeiling, a.approach, a.date, a.quality, a.firmness].filter(
    (f) => f.needsChoice,
  ) as FieldAssumption<unknown>[];
}

// ── Workload at two grains (§3.3) ─────────────────────────────────────────────

/** Hours in one capacity unit (glossary "Capacity Unit (CU)", ≈ 8 hours). */
export const HOURS_PER_CU = 8;

/**
 * Rough cut: capacity units per team. Each line's own source multiplier is
 * applied here, once (canon 111 §3.5: never baked into stored allocations).
 */
export function workloadTotals(lines: SourcedWorkloadLine[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const l of lines) {
    totals.set(l.resourceType, (totals.get(l.resourceType) ?? 0) + l.capacityUnits * l.effortMultiplier);
  }
  return totals;
}

export interface WeeklyLoad {
  resourceType: string;
  /** ISO date the week starts: the anchor date plus weekOffset weeks. */
  weekStart: string;
  weekOffset: number;
  hours: number;
  source: AssumptionSource;
}

function addWeeks(isoDate: string, weeks: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

/**
 * Launch plan: each line's total spread evenly over its weeks around its anchor.
 * The weeks always add back up to the rough-cut total, so the two never disagree.
 * A line whose anchor date is unknown is left out (and counted in `unplaced`).
 */
export function workloadByWeek(
  lines: SourcedWorkloadLine[],
  anchors: { start?: string | null; date?: string | null },
  hoursPerCu = HOURS_PER_CU,
): { weeks: WeeklyLoad[]; unplaced: SourcedWorkloadLine[] } {
  const weeks: WeeklyLoad[] = [];
  const unplaced: SourcedWorkloadLine[] = [];
  for (const l of lines) {
    const base = l.anchor === 'date' ? anchors.date : anchors.start;
    if (!base) {
      unplaced.push(l);
      continue;
    }
    const span = l.toWeek - l.fromWeek + 1;
    const perWeek = (l.capacityUnits * l.effortMultiplier * hoursPerCu) / span;
    for (let w = l.fromWeek; w <= l.toWeek; w++) {
      weeks.push({ resourceType: l.resourceType, weekStart: addWeeks(base, w), weekOffset: w, hours: perWeek, source: l.source });
    }
  }
  return { weeks, unplaced };
}

// ── Planning's predictability inputs (replaces type-name matching) ────────────

export interface PatternPredictability {
  uncertainty_factor: number;
  confidence_ceiling: number;
  commitment_firmness: number;
  suggestedFlexibility: 'fixed' | 'flexible';
  slackWeeks: number;
  calendarConfidence: number;
  rationale: string;
}

const FIRMNESS_FROM_DATE: Record<DateFirmness, number> = { fixed: 8, target: 4, flexible: 1 };

/**
 * What the time-phased planner reads, from an item's patterns. Null when no
 * type the item has states any of it — the caller keeps today's behavior.
 * slackWeeks ↔ uncertainty_factor uses calendarPredictability's own relation
 * (slackWeeks = round((factor − 1) × 2)).
 */
export function predictabilityFromAssumptions(
  a: ItemAssumptions,
  fallback: { uncertainty_factor: number; confidence_ceiling: number; commitment_firmness: number },
): PatternPredictability | null {
  const slack = a.slackWeeks.effective;
  const ceiling = a.confidenceCeiling.effective;
  const date = a.date.effective;
  const firm = a.firmness.effective;
  if (slack === undefined && ceiling === undefined && date === undefined && firm === undefined) return null;

  const slackWeeks = slack ?? Math.max(0, Math.round((fallback.uncertainty_factor - 1) * 2));
  const confidence = ceiling ?? fallback.confidence_ceiling;
  const firmness = firm ?? (date ? FIRMNESS_FROM_DATE[date] : fallback.commitment_firmness);
  const from = (f: FieldAssumption<unknown>) =>
    f.override ? 'this item' : f.stated.map((s) => s.source.typeLabel).join(' / ');
  const parts: string[] = [];
  if (date) parts.push(`date ${date} (${from(a.date as FieldAssumption<unknown>)})`);
  if (slack !== undefined) parts.push(`${slack} weeks of slack (${from(a.slackWeeks as FieldAssumption<unknown>)})`);
  if (ceiling !== undefined) parts.push(`confidence up to ${Math.round(ceiling * 100)}% (${from(a.confidenceCeiling as FieldAssumption<unknown>)})`);
  const choose = fieldsNeedingChoice(a).length;
  return {
    uncertainty_factor: 1 + slackWeeks / 2,
    confidence_ceiling: confidence,
    commitment_firmness: firmness,
    suggestedFlexibility: date === 'fixed' ? 'fixed' : 'flexible',
    slackWeeks,
    calendarConfidence: confidence,
    rationale: `From patterns: ${parts.join('; ')}${choose ? ` — ${choose} still to settle on the item` : ''}`,
  };
}

export const PART_LABEL: Record<PatternPart, string> = {
  certainty: 'Certainty',
  shape: 'Shape',
  workload: 'Workload',
  bar: 'Bar',
};

export const DATE_FIRMNESS_LABEL: Record<DateFirmness, string> = {
  fixed: 'Fixed date',
  target: 'Target date',
  flexible: 'Flexible',
};

export const QUALITY_LABEL: Record<QualityBar, string> = {
  polished: 'Polished',
  good_enough: 'Good enough',
  rough: 'Rough is fine',
};

export const ANCHOR_LABEL: Record<PatternAnchor, string> = {
  start: "the item's start",
  date: "the item's date",
};

/** A pattern in one line, for lists: "Fixed date · Polished · 2 stages · 2 teams, 26 CU". */
export function patternSummary(p: PatternParts | null): string {
  if (!p || isEmptyPattern(p)) return 'No pattern yet';
  const bits: string[] = [];
  if (p.bar.date) bits.push(DATE_FIRMNESS_LABEL[p.bar.date]);
  if (p.bar.quality) bits.push(QUALITY_LABEL[p.bar.quality]);
  if (p.certainty.scheduleSlackWeeks !== undefined) bits.push(`${p.certainty.scheduleSlackWeeks} wk slack`);
  if (p.shape.stages?.length) bits.push(`${p.shape.stages.length} stage${p.shape.stages.length === 1 ? '' : 's'}`);
  if (p.workload.lines?.length) {
    const teams = new Set(p.workload.lines.map((l) => l.resourceType)).size;
    const cu = p.workload.lines.reduce((s, l) => s + l.capacityUnits, 0);
    bits.push(`${teams} team${teams === 1 ? '' : 's'}, ${cu} CU`);
  }
  return bits.join(' · ') || 'Pattern set';
}
